import { Trans, useTranslation } from 'react-i18next';
import { rewardFromLabel, type GuestNudge } from '../format';
import { NUDGE_EMPHASIS } from './nudgeEmphasis';
import '../loyalty.css';

interface Props {
  nudge: GuestNudge;
  onLogin: () => void;
  onRegister: () => void;
  onDismiss: () => void;
}

/**
 * SC-1091 — the banner above the checkout steps for a guest: « Connectez-vous et
 * gagnez ~N pts sur cette commande ». Dismissible (for the session — the page keeps
 * the flag). Pay-on-site earns nothing by itself, so the banner then says to show
 * the card at the counter, with no number.
 *
 * Une seule phrase et deux boutons compacts : la page ne l'affiche qu'à la première
 * étape — l'étape « Coordonnées » porte sa propre invitation, et la répéter sur
 * chaque écran de l'assistant noyait le parcours sous le même message.
 */
export default function GuestPointsBanner({ nudge, onLogin, onRegister, onDismiss }: Props) {
  const { t, i18n } = useTranslation();
  return (
    <section
      data-testid="guest-points-banner"
      aria-label={t('loyalty.card.title')}
      className="loyalty-scope relative rounded-btn-sm border-2 border-solid border-ink bg-cream py-2 ps-3 pe-11"
    >
      <p className="m-0 font-mono text-sm leading-relaxed text-ink">
        <span aria-hidden="true">🎁 </span>
        {nudge.kind === 'points' ? (
          <>
            <Trans
              i18nKey="loyalty.guest.banner"
              values={{ n: nudge.points }}
              components={NUDGE_EMPHASIS}
            />
            {/* SC-1640 — ce que valent ces points : la récompense la moins chère. */}
            {nudge.reward && ` ${rewardFromLabel(t, nudge.reward, i18n.language)}`}
          </>
        ) : (
          <Trans i18nKey="loyalty.guest.bannerCounter" components={NUDGE_EMPHASIS} />
        )}
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          type="button"
          onClick={onLogin}
          data-testid="guest-banner-login"
          className="min-h-[44px] rounded-btn-sm border-2 border-solid border-ink bg-mint px-4 font-display text-sm text-ink"
        >
          {t('loyalty.guest.login')}
        </button>
        <button
          type="button"
          onClick={onRegister}
          data-testid="guest-banner-register"
          className="min-h-[44px] border-0 bg-transparent p-0 font-mono text-sm text-ink underline"
        >
          {t('loyalty.guest.register')}
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('loyalty.guest.dismiss')}
        data-testid="guest-banner-dismiss"
        className="absolute end-0 top-0 flex h-11 w-11 items-center justify-center border-0 bg-transparent p-0 font-display text-xl text-ink"
      >
        <span aria-hidden="true">×</span>
      </button>
    </section>
  );
}
